import React, { useState, useRef } from 'react';
import { fmtNum, fmtFull, fmtMoney } from '../utils/formatters';

interface SeriesConfig {
  key: string;
  label: string;
  color: string;
  isMoney?: boolean;
  sensor?: boolean;
}

interface ChartDataPoint {
  label: string;
  [key: string]: any;
}

interface TimeSeriesChartProps {
  data: ChartDataPoint[];
  seriesConfig: SeriesConfig[];
  activeKeys: string[];
  height?: number;
  isMoney?: boolean;
  // Whether the tooltip appends the current year after each point's label
  // (e.g. "Aug 15" -> "Aug 15 2026"). Suited to date-based labels; turn off
  // for non-date labels like hour-of-day ("6a") where a year makes no sense.
  showYear?: boolean;
}

export function TimeSeriesChart({ data, seriesConfig, activeKeys, height = 280, isMoney = false, showYear = true }: TimeSeriesChartProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(900);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [mouseX, setMouseX] = useState<number | null>(null);

  React.useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const padding = { top: 18, right: 24, bottom: 30, left: 52 };
  const W = Math.max(width, 320);
  const H = height;
  const innerW = W - padding.left - padding.right;
  const innerH = H - padding.top - padding.bottom;

  const visible = seriesConfig.filter(s => activeKeys.includes(s.key));
  const allVals = visible.flatMap(s => data.map((d: any) => d[s.key]));
  const yMax = Math.max(1, ...allVals) * 1.1;
  const yMin = 0;

  const x = (i: number) => padding.left + (i / Math.max(1, data.length - 1)) * innerW;
  const y = (v: number) => padding.top + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const linePath = (key: string) => {
    if (data.length === 1) {
      const yv = y((data[0] as any)[key]).toFixed(2);
      return `M ${padding.left.toFixed(2)} ${yv} L ${(padding.left + innerW).toFixed(2)} ${yv}`;
    }
    return data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(2)} ${y((d as any)[key]).toFixed(2)}`).join(' ');
  };

  const areaPath = (key: string) => {
    const base = (padding.top + innerH).toFixed(2);
    if (data.length === 1) {
      const yv = y((data[0] as any)[key]).toFixed(2);
      return `M ${padding.left.toFixed(2)} ${yv} L ${(padding.left + innerW).toFixed(2)} ${yv} L ${(padding.left + innerW).toFixed(2)} ${base} L ${padding.left.toFixed(2)} ${base} Z`;
    }
    const top = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(2)} ${y((d as any)[key]).toFixed(2)}`).join(' ');
    return `${top} L ${x(data.length - 1).toFixed(2)} ${base} L ${x(0).toFixed(2)} ${base} Z`;
  };

  const yTicks: number[] = [];
  for (let i = 0; i <= 4; i++) yTicks.push((yMax * i) / 4);

  const xTickEvery = Math.max(1, Math.floor(data.length / 6));
  const xTicks = data.map((d, i) => ({ d, i })).filter(({ i }) => i % xTickEvery === 0 || i === data.length - 1);

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (data.length === 0) { setHoverIdx(null); setMouseX(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    if (mx < padding.left || mx > padding.left + innerW) { setHoverIdx(null); setMouseX(null); return; }
    setMouseX(mx);
    const idx = Math.round(((mx - padding.left) / innerW) * (data.length - 1));
    setHoverIdx(Math.max(0, Math.min(data.length - 1, idx)));
  };

  const formatVal = (v: any, key: string) => {
    if (key === 'revenue') return fmtMoney(v);
    return fmtFull(v);
  };

  return (
    <div ref={wrapRef} style={{ width: '100%', position: 'relative' }}>
      <svg width={W} height={H} onMouseMove={onMove} onMouseLeave={() => { setHoverIdx(null); setMouseX(null); }} style={{ display: 'block', cursor: 'crosshair' }}>
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={padding.left} x2={padding.left + innerW} y1={y(v)} y2={y(v)}
              stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray={i === 0 ? '0' : '2 4'} />
            <text x={padding.left - 8} y={y(v)} textAnchor="end" dominantBaseline="middle"
              fontSize="11" fill="var(--fg-faint)" className="num">
              {isMoney ? '$' + fmtNum(v) : fmtNum(v)}
            </text>
          </g>
        ))}

        {xTicks.map(({ d, i }) => (
          <text key={i} x={x(i)} y={H - 10} textAnchor="middle" fontSize="11" fill="var(--fg-faint)" className="num">
            {d.label}
          </text>
        ))}

        {visible.length === 1 && (
          <path d={areaPath(visible[0].key)} fill={visible[0].color} opacity="0.10" />
        )}

        {visible.map(s => (
          <path key={s.key} d={linePath(s.key)} fill="none"
            stroke={s.color} strokeWidth="2.25" strokeLinejoin="round" strokeLinecap="round" />
        ))}

        {hoverIdx != null && data[hoverIdx] && data.length > 1 && (
          <g>
            <line x1={x(hoverIdx)} x2={x(hoverIdx)} y1={padding.top} y2={padding.top + innerH}
              stroke="var(--fg-muted)" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
            {visible.map(s => (
              <circle key={s.key} cx={x(hoverIdx)} cy={y((data[hoverIdx] as any)[s.key])} r="4.5"
                fill="var(--bg-surface)" stroke={s.color} strokeWidth="2.5" />
            ))}
          </g>
        )}
      </svg>

      {hoverIdx != null && data[hoverIdx] && (
        <div style={{
          position: 'absolute', top: 12,
          left: Math.min(Math.max((data.length === 1 ? mouseX ?? W / 2 : x(hoverIdx)) - 90, 60), W - 200),
          background: 'var(--bg-surface)', border: '1px solid var(--border-soft)',
          borderRadius: 10, boxShadow: 'var(--shadow-card-hi)',
          padding: '10px 12px', minWidth: 180, pointerEvents: 'none', fontSize: 12,
        }}>
          <div style={{ fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 8, fontSize: 12 }}>
            {data[hoverIdx].label}{showYear ? ' 2026' : ''}
          </div>
          {visible.map(s => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ width: 8, height: 8, background: s.color, borderRadius: 2, display: 'inline-block' }} />
              <span style={{ color: 'var(--fg-muted)', flex: 1 }}>{s.label}</span>
              <span className="num" style={{ fontWeight: 700, color: 'var(--fg-strong)' }}>
                {formatVal((data[hoverIdx] as any)[s.key], s.key)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
